import type { Theme } from '@mui/material';

export const playgroundThemeStyles = (theme: Theme) => ({
  '.PlaygroundEditorTheme__ltr': { textAlign: 'left' },
  '.PlaygroundEditorTheme__rtl': { textAlign: 'right' },
  '.PlaygroundEditorTheme__paragraph': {
    margin: '0',
    position: 'relative',
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.neutral['900'],
    paddingBottom: '16px',
  },
  '.PlaygroundEditorTheme__quote': {
    margin: '0',
    marginLeft: '20px',
    marginBottom: '10px',
    fontSize: '15px',
    color: 'rgb(101, 103, 107)',
    borderLeftColor: 'rgb(206, 208, 212)',
    borderLeftWidth: '4px',
    borderLeftStyle: 'solid',
    paddingLeft: '16px',
  },
  '.PlaygroundEditorTheme__h1': {
    fontSize: '22px',
    lineHeight: '28px',
    color: theme.palette.neutral['900'],
    fontWeight: 700,
    margin: '0',
    paddingBottom: '16px',
  },
  '.PlaygroundEditorTheme__h2': {
    fontSize: '16px',
    lineHeight: '24px',
    color: theme.palette.neutral['900'],
    fontWeight: 700,
    margin: '0',
    paddingBottom: '8px',
  },
  '.PlaygroundEditorTheme__h3': {
    fontSize: '12px',
    margin: '0',
    textTransform: 'uppercase',
  },
  '.PlaygroundEditorTheme__indent': { '--lexical-indent-base-value': '40px' },
  '.PlaygroundEditorTheme__textBold': { fontWeight: 'bold' },
  '.PlaygroundEditorTheme__textItalic': { fontStyle: 'italic' },
  '.PlaygroundEditorTheme__textUnderline': { textDecoration: 'underline' },
  '.PlaygroundEditorTheme__textStrikethrough': {
    textDecoration: 'line-through',
  },
  '.PlaygroundEditorTheme__textUnderlineStrikethrough': {
    textDecoration: 'underline line-through',
  },
  '.PlaygroundEditorTheme__textSubscript': {
    fontSize: '0.8em',
    verticalAlign: 'sub !important',
  },
  '.PlaygroundEditorTheme__textSuperscript': {
    fontSize: '0.8em',
    verticalAlign: 'super',
  },
  '.PlaygroundEditorTheme__textCode': {
    backgroundColor: 'rgb(240, 242, 245)',
    padding: '1px 0.25rem',
    fontFamily: 'Menlo, Consolas, Monaco, monospace',
    fontSize: '94%',
  },
  '.PlaygroundEditorTheme__hashtag': {
    backgroundColor: 'rgba(88, 144, 255, 0.15)',
    borderBottom: '1px solid rgba(88, 144, 255, 0.3)',
  },
  '.PlaygroundEditorTheme__link': {
    color: 'rgb(33, 111, 219)',
    textDecoration: 'none',
  },
  '.PlaygroundEditorTheme__link:hover': {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  '.PlaygroundEditorTheme__code': {
    backgroundColor: 'rgb(240, 242, 245)',
    fontFamily: 'Menlo, Consolas, Monaco, monospace',
    display: 'block',
    padding: '8px 8px 8px 52px',
    lineHeight: 1.53,
    fontSize: '13px',
    margin: '0',
    marginTop: '8px',
    marginBottom: '8px',
    overflowX: 'auto',
    position: 'relative',
    tabSize: 2,
  },
  '.PlaygroundEditorTheme__code:before': {
    content: 'attr(data-gutter)',
    position: 'absolute',
    backgroundColor: '#eee',
    left: '0',
    top: '0',
    borderRight: '1px solid #ccc',
    padding: '8px',
    color: '#777',
    whiteSpace: 'pre-wrap',
    textAlign: 'right',
    minWidth: '25px',
  },
  '.PlaygroundEditorTheme__table': {
    borderCollapse: 'collapse',
    borderSpacing: '0',
    overflowY: 'scroll',
    overflowX: 'scroll',
    tableLayout: 'fixed',
    width: 'max-content',
    margin: '30px 0',
  },
  '.PlaygroundEditorTheme__tableSelection *::selection': {
    backgroundColor: 'transparent',
  },
  '.PlaygroundEditorTheme__tableSelected': {
    outline: '2px solid rgb(60, 132, 244)',
  },
  '.PlaygroundEditorTheme__tableCell': {
    border: '1px solid #bbb',
    width: '75px',
    minWidth: '75px',
    verticalAlign: 'top',
    textAlign: 'start',
    padding: '6px 8px',
    position: 'relative',
    outline: 'none',
  },
  '.PlaygroundEditorTheme__tableCellSortedIndicator': {
    display: 'block',
    opacity: 0.5,
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '100%',
    height: '4px',
    backgroundColor: '#999',
  },
  '.PlaygroundEditorTheme__tableCellResizer': {
    position: 'absolute',
    right: '-4px',
    height: '100%',
    width: '8px',
    cursor: 'ew-resize',
    zIndex: 10,
    top: '0',
  },
  '.PlaygroundEditorTheme__tableCellHeader': {
    textAlign: 'start',
  },
  '.PlaygroundEditorTheme__tableCellSelected': { backgroundColor: '#c9dbf0' },
  '.PlaygroundEditorTheme__tableCellPrimarySelected': {
    border: '2px solid rgb(60, 132, 244)',
    display: 'block',
    height: 'calc(100% - 2px)',
    position: 'absolute',
    width: 'calc(100% - 2px)',
    left: '-1px',
    top: '-1px',
    zIndex: 2,
  },
  '.PlaygroundEditorTheme__tableCellEditing': {
    boxShadow: '0 0 5px rgba(0, 0, 0, 0.4)',
    borderRadius: '3px',
  },
  '.PlaygroundEditorTheme__tableAddColumns': {
    position: 'absolute',
    top: '0',
    width: '20px',
    backgroundColor: '#eee',
    height: '100%',
    right: '-25px',
    animation: 'table-controls 0.2s ease',
    border: '0',
    cursor: 'pointer',
  },
  '.PlaygroundEditorTheme__tableAddColumns:after': {
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    display: 'block',
    content: "' '",
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    opacity: 0.4,
  },
  '.PlaygroundEditorTheme__tableAddColumns:hover': {
    backgroundColor: '#c9dbf0',
  },
  '.PlaygroundEditorTheme__tableAddRows': {
    position: 'absolute',
    bottom: '-25px',
    width: 'calc(100% - 25px)',
    backgroundColor: '#eee',
    height: '20px',
    left: '0',
    animation: 'table-controls 0.2s ease',
    border: '0',
    cursor: 'pointer',
  },
  '.PlaygroundEditorTheme__tableAddRows:after': {
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    display: 'block',
    content: "' '",
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    opacity: 0.4,
  },
  '.PlaygroundEditorTheme__tableAddRows:hover': { backgroundColor: '#c9dbf0' },
  '@keyframes table-controls': { '0%': { opacity: 0 }, '100%': { opacity: 1 } },
  '.PlaygroundEditorTheme__tableCellResizeRuler': {
    display: 'block',
    position: 'absolute',
    width: '1px',
    backgroundColor: 'rgb(60, 132, 244)',
    height: '100%',
    top: '0',
  },
  '.PlaygroundEditorTheme__tableCellActionButtonContainer': {
    display: 'block',
    right: '5px',
    top: '6px',
    position: 'absolute',
    zIndex: 4,
    width: '20px',
    height: '20px',
  },
  '.PlaygroundEditorTheme__tableCellActionButton': {
    backgroundColor: '#eee',
    display: 'block',
    border: '0',
    borderRadius: '20px',
    width: '20px',
    height: '20px',
    color: '#222',
    cursor: 'pointer',
  },
  '.PlaygroundEditorTheme__tableCellActionButton:hover': {
    backgroundColor: '#ddd',
  },
  '.PlaygroundEditorTheme__characterLimit': {
    display: 'inline',
    backgroundColor: '#ffbbbb !important',
  },
  '.PlaygroundEditorTheme__ol1': {
    padding: '0',
    margin: '0',
    listStylePosition: 'outside',
  },
  '.PlaygroundEditorTheme__ol2': {
    padding: '0',
    margin: '0',
    listStyleType: 'upper-alpha',
    listStylePosition: 'outside',
  },
  '.PlaygroundEditorTheme__ol3': {
    padding: '0',
    margin: '0',
    listStyleType: 'lower-alpha',
    listStylePosition: 'outside',
  },
  '.PlaygroundEditorTheme__ol4': {
    padding: '0',
    margin: '0',
    listStyleType: 'upper-roman',
    listStylePosition: 'outside',
  },
  '.PlaygroundEditorTheme__ol5': {
    padding: '0',
    margin: '0',
    listStyleType: 'lower-roman',
    listStylePosition: 'outside',
  },
  '.PlaygroundEditorTheme__ul': {
    padding: '0',
    margin: '0',
    listStylePosition: 'outside',
    listStyleType: 'disc',
  },
  '.PlaygroundEditorTheme__listItem': { margin: '0 32px' },
  '.PlaygroundEditorTheme__listItemChecked, .PlaygroundEditorTheme__listItemUnchecked': {
    position: 'relative',
    marginLeft: '8px',
    marginRight: '8px',
    paddingLeft: '24px',
    paddingRight: '24px',
    listStyleType: 'none',
    outline: 'none',
  },
  '.PlaygroundEditorTheme__listItemChecked': { textDecoration: 'line-through' },
  '.PlaygroundEditorTheme__listItemUnchecked:before, .PlaygroundEditorTheme__listItemChecked:before': {
    content: "''",
    width: '16px',
    height: '16px',
    top: '2px',
    left: '0',
    cursor: 'pointer',
    display: 'block',
    backgroundSize: 'cover',
    position: 'absolute',
  },
  ".PlaygroundEditorTheme__listItemUnchecked[dir='rtl']:before, .PlaygroundEditorTheme__listItemChecked[dir='rtl']:before":
    {
      left: 'auto',
      right: '0',
    },
  '.PlaygroundEditorTheme__listItemUnchecked:focus:before, .PlaygroundEditorTheme__listItemChecked:focus:before': {
    boxShadow: '0 0 0 2px #a6cdfe',
    borderRadius: '2px',
  },
  '.PlaygroundEditorTheme__listItemUnchecked:before': {
    border: '1px solid #999',
    borderRadius: '2px',
  },
  '.PlaygroundEditorTheme__listItemChecked:before': {
    border: '1px solid rgb(61, 135, 245)',
    borderRadius: '2px',
    backgroundColor: '#3d87f5',
    backgroundRepeat: 'no-repeat',
  },
  '.PlaygroundEditorTheme__listItemChecked:after': {
    content: "''",
    cursor: 'pointer',
    borderColor: '#fff',
    borderStyle: 'solid',
    position: 'absolute',
    display: 'block',
    top: '6px',
    width: '3px',
    left: '7px',
    right: '7px',
    height: '6px',
    transform: 'rotate(45deg)',
    borderWidth: '0 2px 2px 0',
  },
  '.PlaygroundEditorTheme__nestedListItem': { listStyleType: 'none' },
  '.PlaygroundEditorTheme__nestedListItem:before, .PlaygroundEditorTheme__nestedListItem:after': {
    display: 'none',
  },
  '.PlaygroundEditorTheme__tokenComment': { color: 'slategray' },
  '.PlaygroundEditorTheme__tokenPunctuation': { color: '#999' },
  '.PlaygroundEditorTheme__tokenProperty': { color: '#905' },
  '.PlaygroundEditorTheme__tokenSelector': { color: '#690' },
  '.PlaygroundEditorTheme__tokenOperator': { color: '#9a6e3a' },
  '.PlaygroundEditorTheme__tokenAttr': { color: '#07a' },
  '.PlaygroundEditorTheme__tokenVariable': { color: '#e90' },
  '.PlaygroundEditorTheme__tokenFunction': { color: '#dd4a68' },
  '.PlaygroundEditorTheme__mark': {
    background: 'rgba(255, 212, 0, 0.14)',
    borderBottom: '2px solid rgba(255, 212, 0, 0.3)',
    paddingBottom: '2px',
  },
  '.PlaygroundEditorTheme__markOverlap': {
    background: 'rgba(255, 212, 0, 0.3)',
    borderBottom: '2px solid rgba(255, 212, 0, 0.7)',
  },
  '.PlaygroundEditorTheme__mark.selected': {
    background: 'rgba(255, 212, 0, 0.5)',
    borderBottom: '2px solid rgba(255, 212, 0, 1)',
  },
  '.PlaygroundEditorTheme__markOverlap.selected': {
    background: 'rgba(255, 212, 0, 0.7)',
    borderBottom: '2px solid rgba(255, 212, 0, 0.7)',
  },
  '.PlaygroundEditorTheme__embedBlock': { userSelect: 'none' },
  '.PlaygroundEditorTheme__embedBlockFocus': {
    outline: '2px solid rgb(60, 132, 244)',
  },
  '.PlaygroundEditorTheme__layoutContainer': {
    display: 'grid',
    gap: '10px',
    margin: '10px 0',
  },
  '.PlaygroundEditorTheme__layoutItem': {
    border: '1px dashed #ddd',
    padding: '8px 16px',
  },
  '.PlaygroundEditorTheme__autocomplete': { color: '#ccc' },
  '.PlaygroundEditorTheme__hr': {
    padding: '2px 2px',
    border: 'none',
    margin: '1em 0',
    cursor: 'pointer',
  },
  '.PlaygroundEditorTheme__hr:after': {
    content: "''",
    display: 'block',
    height: '2px',
    backgroundColor: '#ccc',
    lineHeight: '2px',
  },
  '.PlaygroundEditorTheme__hr.selected': {
    outline: '2px solid rgb(60, 132, 244)',
    userSelect: 'none',
  },
});
